import { debugLogEvent, isAnalyticsDebugEnabled } from './debug'
import { addScript, errorHandler } from './utils'

type ConsentChangeListener = (event: unknown) => void

const consentChangeListeners = new Set<ConsentChangeListener>()

/**
 * Subscribes to OneTrust consent changes (D5). Listeners fire whenever the
 * visitor changes their consent after the banner has loaded. Errors in a
 * listener are logged and swallowed — a consumer's handler must never break
 * another's, nor the page.
 * @returns an unsubscribe function
 */
export const onConsentChanged = (
  listener: ConsentChangeListener,
): (() => void) => {
  consentChangeListeners.add(listener)
  return () => consentChangeListeners.delete(listener)
}

const notifyConsentChanged = (event: unknown): void => {
  if (isAnalyticsDebugEnabled()) {
    debugLogEvent('consent', 'changed', event)
  }
  for (const listener of consentChangeListeners) {
    try {
      listener(event)
    } catch (error) {
      errorHandler(error as Error)
    }
  }
}

/**
 * Insert the OneTrust cookie tracking script.
 *
 * The environment decides the domain-script suffix: production uses the bare
 * OneTrust id, every other environment its `-test` variant. The caller's
 * typed environment is the source of truth — never the URL (D6).
 *
 * `OptanonWrapper` is a real subscription (D5): once OneTrust is ready it
 * registers `OnConsentChanged`, so mid-session consent changes reach
 * {@link onConsentChanged} listeners instead of vanishing. This holds only when
 * this helper inserts the scripts: when the page already carries OneTrust, the
 * wrapper belongs to that host and is not registered here.
 *
 * @param oneTrustId - The OneTrust domain-script id
 * @param isProduction - Production loads the bare script; anything else `-test`
 */
export const insertCookieTrackingScript = (
  oneTrustId: string,
  isProduction = false,
): void => {
  if (
    !oneTrustId ||
    typeof window === 'undefined' ||
    window.location.href?.includes('localhost') ||
    window.cookieTrackingScriptInserted ||
    // Consent belongs to the host (§3). A page that already carries OneTrust —
    // via this helper, a plain tag, or GTM — must not get a second domain
    // script stacked on its own.
    'OneTrust' in window ||
    'Optanon' in window ||
    // The globals only exist once the host's async stub has run. Before that
    // its tag is already in the DOM, possibly keyed to another domain-script
    // id (SFCC PDP, GTM, Shopify). A second OtAutoBlock breaks Segment's own
    // script load ("Cannot redefine property: src", SPOC-1436, FC-198).
    document.querySelector(
      'script[src*="cdn.cookielaw.org"], script[data-domain-script]',
    ) !== null
  ) {
    return
  }

  // The caller resolves this from the surface's typed environment, never from
  // the URL (D6) — a host forwarding 'prd'/'Prd' must not load the -test
  // consent script on production (review finding, PR 6820).
  const dataDomainScript = `${oneTrustId}${isProduction ? '' : '-test'}`

  addScript(
    `https://cdn.cookielaw.org/consent/${dataDomainScript}/OtAutoBlock.js`,
  )
  addScript('https://cdn.cookielaw.org/scripttemplates/otSDKStub.js', {
    'data-document-language': 'true',
    'data-ot-ignore': 'true',
    'charSet': 'UTF-8',
    'data-domain-script': `${dataDomainScript}`,
  })
  window.OptanonWrapper = () => {
    try {
      window.OneTrust?.OnConsentChanged?.(notifyConsentChanged)
    } catch (error) {
      errorHandler(error as Error)
    }
  }
  window.cookieTrackingScriptInserted = true
}
